import { Divider, Stack } from '@/components/layout';
import { Button, Pagination, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { ListPlayersHeaderProps } from '@/features/player/components/list-player/list-players-header/common';
import {
  useListPlayersHeaderCommon,
  useStyles,
} from '@/features/player/components/list-player/list-players-header/common';
import { SearchBar } from '@/features/player/components/list-player/search-bar';
import { PlayerFilterSheet } from '@/features/player/components/player-filter-sheet';
import { PlayerSortSheet } from '@/features/player/components/player-sort-sheet';
import { useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { Activity, memo, PropsWithChildren, useCallback } from 'react';
import { StyleSheet } from 'react-native';

export const CreateProfileButton = memo(() => {
  const styles = useStyles();
  const rootNavigation = useNavigation();

  const onCreateProfile = useCallback(() => {
    rootNavigation.navigate('Auth', { screen: 'MutatePlayer' });
  }, [rootNavigation]);

  return (
    <Button
      onPress={onCreateProfile}
      style={[styles.createProfileButton, styles.tabletCreateButton]}>
      <Button.Text style={styles.createProfileButtonText} transform="uppercase">
        {STRINGS.player.listPlayers.createProfileButton}
      </Button.Text>
    </Button>
  );
});

export const ListPlayersHeaderTablet = ({
  onChangeSearchText,
  onClearSearchText,
  searchText,
  paginatedMeta,
  onChangePage,
  titleResultsSummary,
  children,
  extraComponent,
  isResultsSummaryVisible = true,
}: PropsWithChildren<ListPlayersHeaderProps>) => {
  const styles = useStyles();
  const { scale } = useScaling();
  const {
    totalPages,
    currentPage,
    resultSummary,
    open,
    setOpen,
    level,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,
    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
  } = useListPlayersHeaderCommon({ paginatedMeta, isResultsSummaryVisible });

  return (
    <Stack paddingBottom={scale(14)}>
      <Stack
        direction="row"
        align="center"
        width="100%"
        gap={scale(12)}
        paddingX={scale({ phone: 18, tablet: 0 })}>
        <Stack direction="row" align="center" gap={scale(12)} style={styles.tabletActionsContainer}>
          {/* TODO: need refactor, move logic to component sort and filter player */}
          <PlayerFilterSheet
            open={open}
            onOpenChange={setOpen}
            filterButtonLabel={filterButtonLabel}
            closeWithoutSave={closeWithoutSave}
            saveAndClose={() => {
              onChangePage?.(1);
              saveAndClose();
            }}
            levels={LEVELS}
            draftLevel={draftLevel}
            onDraftLevelChange={setDraftLevel}
            lastActiveOptions={lastActiveOptions}
            draftLastActive={draftLastActive}
            onDraftLastActiveChange={setDraftLastActive}
          />

          {/* TODO: need refactor, move logic to component sort and filter player */}
          <PlayerSortSheet
            open={sortOpen}
            onOpenChange={setSortOpen}
            sortButtonLabel={sortButtonLabel}
            closeWithoutSave={closeSortWithoutSave}
            saveAndClose={() => {
              onChangePage?.(1);
              saveSortAndClose();
            }}
            levelFilter={level}
            saved={{ key: sortKey, order: sortOrder }}
            draft={sortDraft}
            setDraft={setSortDraft}
          />
        </Stack>

        <Stack
          flexShrink={0}
          flex={extraComponent ? 1 : undefined}
          width={extraComponent ? undefined : scale(360)}>
          <SearchBar
            placeholder={STRINGS.player.listPlayers.searchPlaceholder}
            onChangeText={onChangeSearchText}
            onClear={onClearSearchText}
            value={searchText}
          />
        </Stack>

        {!extraComponent ? <Stack flex={1} /> : null}

        {children}
        {extraComponent}
      </Stack>

      <Divider thickness={StyleSheet.hairlineWidth} color={colors['6']} spacing={scale(12)} />

      <Stack
        direction="row"
        width="100%"
        paddingX={scale({ phone: 18, tablet: 0 })}
        justify="space-between"
        align="center">
        <Activity mode={isResultsSummaryVisible ? 'visible' : 'hidden'}>
          <Text
            variant="Font Inter/Subtitle/Bold 14px"
            transform="uppercase"
            style={{
              color: colors.Blue,
              minWidth: scale(200),
            }}>
            {titleResultsSummary}
          </Text>
        </Activity>
        <Stack flex={1} align="center">
          {totalPages ? (
            <Pagination
              currentPage={currentPage ?? 1}
              totalPages={totalPages}
              onPageChange={onChangePage}
            />
          ) : null}
        </Stack>
        <Activity mode={resultSummary ? 'visible' : 'hidden'}>
          <Text
            variant="Font Inter/Subtitle/Regular 11px"
            style={[styles.resultsSummary, styles.tabletResultsSummary, styles.summaryText]}>
            {resultSummary}
          </Text>
        </Activity>
      </Stack>
    </Stack>
  );
};
